import * as React from 'react'
import styled from 'styled-typed'
import DayPicker, { Modifiers, DayModifiers } from 'react-day-picker'
import { transparency } from 'utils/css'
import { isBefore, startOfDay, endOfDay } from 'date-fns'
import { createDateRange, DateRange } from 'dateRanges'
import { areDateRangeDatesEqual } from 'reports/helpers'

interface PickerProps {
    rangeColor: string
}

const Picker = styled(DayPicker)<PickerProps>`
    &.DayPicker {
        font-size: 0.875em;
        align-self: flex-start;
        margin-top: 0;
        margin-left: 1em;
        min-height: 20.5em;
        min-width: 38.6em;
    }
    &.DayPicker:first-child {
        margin-top: 1em;
    }
    .DayPicker-wrapper:focus {
        outline: none;
    }
    .DayPicker-NavButton:focus {
        outline: none;
    }
    .DayPicker-NavButton--prev {
        left: 1.5em;
    }
    .DayPicker-NavButton--next {
        right: 1.6em;
    }
    .DayPicker-Month {
        position: relative;
        margin-top: 4em;
    }
    .DayPicker-Caption {
        margin-bottom: 1.5em;
        font-weight: normal;
        position: absolute;
        left: 4em;
        top: -2.9em;
    }
    .DayPicker-Weekdays {
        margin-top: 5em;
        font-weight: 500;
        color: ${(props) => props.theme.colors.textDark};
    }
    .DayPicker-Day:focus {
        outline: none;
    }
    .DayPicker-Day {
        font-size: 0.875em;
        padding: 0.15em 0 0 0;
        width: 2.7em;
        height: 2.5em;
        position: relative;
        border: 2px solid ${(props) => props.theme.colors.white};
        border-radius: 6px;
        color: ${(props) => transparency(props.theme.colors.textDark, 0.7)};
    }
    .DayPicker-Day:not(.DayPicker-Day--outside):not(.DayPicker-Day--selected)::after {
        display: block;
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        border: 2px solid ${(props) => props.theme.colors.background};
        border-radius: 6px;
    }
    .DayPicker-Day--today {
        color: inherit;
        font-weight: normal;
    }
    &&& .DayPicker-Day--selected:not(.DayPicker-Day--outside),
    &&& .DayPicker-Day--selected:not(.DayPicker-Day--outside):hover {
        background: ${(props) => transparency(props.rangeColor, 0.4)};
        color: ${(props) => transparency(props.theme.colors.textDark, 0.8)};
        border-radius: 8px;
    }
    &&& .DayPicker-Day--edgeDate:not(.DayPicker-Day--outside),
    &&& .DayPicker-Day--edgeDate:not(.DayPicker-Day--outside):hover {
        background: ${(props) => props.rangeColor};
        color: ${(props) => props.theme.colors.white};
    }
    &&& .DayPicker-Day--disabled:not(.DayPicker-Day--outside),
    &&& .DayPicker-Day--disabled:not(.DayPicker-Day--outside):hover {
        color: ${(props) => transparency(props.theme.colors.textDark, 0.3)};
    }
`

interface DateRangePickerProps {
    rangeColor: string
    dateRange: DateRange
    disabledDays: { after: Date }
    modifiers?: Partial<Modifiers>
    month: Date
    fromMonth: Date
    toMonth: Date
    onDateRangeChange: (dateRange: DateRange) => void
}

interface DateRangePickerState {
    dateRange: DateRange
    newFrom: Date | null
    mouseOverTo: Date | null
}

export class DateRangePicker extends React.Component<DateRangePickerProps, DateRangePickerState> {
    constructor(props: DateRangePickerProps) {
        super(props)
        this.state = {
            dateRange: this.props.dateRange,
            newFrom: null,
            mouseOverTo: null,
        }
    }

    componentDidUpdate(prevProps: DateRangePickerProps) {
        if (!areDateRangeDatesEqual(prevProps.dateRange, this.props.dateRange)) {
            this.setState({ dateRange: this.props.dateRange })
        }
    }

    handleDayMouseEnter = (day: Date, modifiers: DayModifiers) => {
        if (modifiers.disabled) {
            return
        }
        if (this.state.newFrom) {
            this.setState({ mouseOverTo: day })
        }
    }

    handleDayClick = (day: Date, modifiers: DayModifiers) => {
        if (modifiers.disabled) {
            return
        }
        const { newFrom, mouseOverTo } = this.state
        if (!newFrom) {
            this.setState({ newFrom: day, mouseOverTo: day })
        } else if (newFrom && mouseOverTo) {
            const from = isBefore(newFrom, mouseOverTo) ? newFrom : mouseOverTo
            const to = isBefore(newFrom, mouseOverTo) ? mouseOverTo : newFrom
            const dateRange = createDateRange(startOfDay(from), endOfDay(to))
            this.setState({ dateRange, newFrom: null })
            this.props.onDateRangeChange(dateRange)
        }
    }

    render() {
        const { newFrom, mouseOverTo, dateRange } = this.state
        const selectedDays =
            newFrom && mouseOverTo ? [{ from: newFrom, to: mouseOverTo }] : [{ from: dateRange.from, to: dateRange.to }]
        const modifiers = {
            ...this.props.modifiers,
            edgeDate: newFrom ? [newFrom, mouseOverTo] : [dateRange.from, dateRange.to],
        }

        return (
            <Picker
                month={this.props.month}
                fromMonth={this.props.fromMonth}
                toMonth={this.props.toMonth}
                numberOfMonths={2}
                firstDayOfWeek={1}
                selectedDays={selectedDays}
                disabledDays={this.props.disabledDays}
                onDayClick={this.handleDayClick}
                onDayMouseEnter={this.handleDayMouseEnter}
                modifiers={modifiers}
                weekdaysShort={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']}
                rangeColor={this.props.rangeColor}
            />
        )
    }
}
